import { useCompleteFragment } from '@eluve/apollo-client';
import { removeEluvePostfix } from '@eluve/llm-outputs';
import { useNamedLogger } from '@eluve/logger';
import { useAnalytics } from '@eluve/posthog-react';
import {
  HumanFriendlyError,
  SYNC_ERRORS_TO_HUMAN_FRIENDLY_ERRORS,
} from '@eluve/vendor';

import { useAppointmentContext } from './appointment.context';
import { appointmentDoctorInteractionFragment } from './operations';
import { useAppointmentName } from './useAppointmentName';
import { useAppointmentPatient } from './useAppointmentPatient';
import { useAppointmentStartDate } from './useAppointmentStartDate';
import { useExternalAppointmentId } from './useExternalAppointmentId';
import { useExternalTemplateId } from './useExternalTemplateId';
import { useSummary } from './useSummary';
import {
  SyncToEhrResponse,
  UseSyncToEhrParams,
  useSyncToEhr,
} from './useSyncToEhr';

type SyncNoteToEhrResponse = SyncToEhrResponse & {
  humanFriendlyError?: HumanFriendlyError;
};

export const useSyncNoteToEhr = (params: UseSyncToEhrParams) => {
  const logger = useNamedLogger(useSyncNoteToEhr.name);
  const analytics = useAnalytics('ehr_sync');

  const { tenantId, appointmentId } = useAppointmentContext();

  const syncToEhr = useSyncToEhr(params);
  const patient = useAppointmentPatient();
  const appointmentExternalId = useExternalAppointmentId();
  const appointmentName = useAppointmentName();
  const { summary, isSummaryAvailable } = useSummary();
  const templateExternalId = useExternalTemplateId();
  const appointmentStartDate = useAppointmentStartDate();

  const data = useCompleteFragment({
    fragment: appointmentDoctorInteractionFragment,
    key: { id: appointmentId },
    strict: false,
  });

  const externalPatientInfo = patient?.external_patients_info?.[0];
  const externalEhr = externalPatientInfo?.external_ehr;

  const patientExternalId = externalPatientInfo?.externalPatientId;

  const interaction = data?.doctor_interaction;
  const additionalUserNotes = interaction?.additionalNotes ?? null;

  return async ({
    shouldSignExternalChart = false,
  }: {
    shouldSignExternalChart: boolean;
  }): Promise<SyncNoteToEhrResponse> => {
    const vendor = externalEhr?.vendor;

    analytics.capture('sync_to_ehr_attempt', {
      appointmentId,
      tenantId,
      vendor,
      shouldSignExternalChart,
      appointmentExternalId,
      templateExternalId,
    });

    try {
      if (summary?.type === 'DYNAMIC') {
        summary.blocks = removeEluvePostfix(summary.blocks);
      }

      if (!isSummaryAvailable) {
        analytics.capture('sync_to_ehr_failed', {
          appointmentId,
          tenantId,
          vendor,
          error: 'SUMMARY_NOT_AVAILABLE',
        });
        return {
          ok: false,
          humanFriendlyError:
            SYNC_ERRORS_TO_HUMAN_FRIENDLY_ERRORS.GENERIC_ERROR,
        };
      }

      const response = await syncToEhr({
        tenantId,
        shouldSignExternalChart,
        appointmentId,
        vendor: externalEhr!.vendor,
        domain: externalEhr!.domain,
        patientExternalId: patientExternalId!,
        appointmentName,
        additionalUserNotes,
        summary: summary!,
        appointmentExternalId,
        templateExternalId: templateExternalId ?? undefined,
        appointmentStartDate,
      });
      logger.info('Sync to EHR response', response);

      if (response.ok) {
        analytics.capture('sync_to_ehr_success', {
          appointmentId,
          tenantId,
          vendor,
          chartId: response.data?.chartId,
        });
      } else {
        analytics.capture('sync_to_ehr_failed', {
          appointmentId,
          tenantId,
          vendor,
          error: response.error,
          humanFriendlyErrorTitle: response.humanFriendlyError?.title,
        });
      }

      return response;
    } catch (error) {
      const errorMessage = (error as Error)?.message;
      logger.error('Error syncing to EHR', {
        error,
        errorMessage,
      });
      analytics.capture('sync_to_ehr_failed', {
        appointmentId,
        tenantId,
        vendor,
        error: 'UNKNOWN_ERROR',
        errorMessage,
      });
      return {
        ok: false,
        humanFriendlyError: SYNC_ERRORS_TO_HUMAN_FRIENDLY_ERRORS.TIMEOUT,
      };
    }
  };
};
